import type {
  AssetBmMemberApiItem,
  AssetBmMemberItem,
  AssignAssetParams,
  BmConfigApiItem,
  BmConfigItem,
  BmMemberApiItem,
  BmMemberItem,
  BmSyncBatchApiItem,
  BmSyncBatchItem,
  BmSyncChangeApiItem,
  BmSyncChangeItem,
  BmSyncChangeType,
  BmSyncTriggerType,
  BmUserAssetApiItem,
  BmUserAssetItem,
  BmUserAssetsResult,
  CreateBmConfigParams,
  EmailMarkApiItem,
  MetaAssetApiItem,
  MetaAssetItem,
  MetaAssetUserApiItem,
  MetaAssetUserItem,
  MetaAssetWriteResponse,
  OperationLogApiItem,
  OperationLogDetailApiItem,
  OperationLogItem,
  OperationModule,
  PagingResult,
  QueryAdAccountResponse,
  QueryAssetBmMembersDataResponse,
  QueryAssetBmMembersParams,
  QueryAssetBmMembersResponse,
  QueryAssetsParams,
  QueryBmListParams,
  QueryBmListResponse,
  QueryBmSyncBatchParams,
  QueryBmSyncBatchResponse,
  QueryBmSyncChangeParams,
  QueryBmSyncChangeResponse,
  QueryBmUserAssetsParams,
  QueryBmUserAssetsResponse,
  QueryBmUsersResponse,
  QueryEmailMarksDataResponse,
  QueryEmailMarksResponse,
  QueryMembersParams,
  QueryOperationLogDetailParams,
  QueryOperationLogDetailResponse,
  QueryOperationLogResponse,
  QueryOperationLogsParams,
  QueryPageDataResponse,
  QueryPageResponse,
  QueryPageResponseItem,
  QueryPixelDataResponse,
  QueryPixelResponse,
  QueryPixelResponseItem,
  SaveBmConfigParams,
  SaveEmailMarkParams,
  ToggleBmSyncParams,
  TokenStateResponse,
  TriggerBmSyncParams,
  TriggerBmSyncResponse,
  UnassignAssetParams,
  UpdateAssetTasksParams,
  ValidateBmTokenParams,
} from './types'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

import {
  toOperationActionCode,
  toOperationModuleCode,
  toOperationResultValue,
} from './operation-log-export'

export { buildOperationLogExportParams } from './operation-log-export'
export * from './types'

function normalizeBmConfig(item: BmConfigApiItem): BmConfigItem {
  return {
    bmId: item.bmId,
    createdAt: item.createdAt,
    enabled: Boolean(item.syncEnabled),
    lastAddedCount: item.lastAddedCount,
    lastRemovedCount: item.lastRemovedCount,
    lastSyncAt: item.lastSyncAt,
    name: item.name,
    remark: item.remark || undefined,
    syncError: item.syncError,
    syncState: item.syncState,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
  }
}

function normalizeBmMember(item: BmMemberApiItem): BmMemberItem {
  const isInternal =
    item.markType === 'INTERNAL' || item.isInternal || item.internal
  let markTypes: string[] = []

  if (item.markTypes?.length) {
    markTypes = item.markTypes.map((markType) => markType.toUpperCase())
  } else if (isInternal) {
    markTypes = ['INTERNAL']
  }

  const markType = markTypes[0] === 'INTERNAL' ? 'INTERNAL' : null

  return {
    assets: (item.assets ?? []).map((asset) => ({
      name: asset.name,
      tasks: asset.tasks,
      type: asset.type,
    })),
    bmId: item.bmId,
    bmName: item.bmName,
    email: item.email,
    markType,
    markTypes,
    markedAt: item.markedAt || undefined,
    markedBy: item.markedBy || undefined,
    name: item.name,
    role: item.role,
    userId: item.userId,
  }
}

function normalizeEmailMark(item: EmailMarkApiItem): BmMemberItem {
  const markTypes = item.markType === 'INTERNAL' ? ['INTERNAL'] : []

  return {
    assets: [],
    bmId: '',
    bmName: '',
    email: item.email,
    markType: markTypes[0] === 'INTERNAL' ? 'INTERNAL' : null,
    markTypes,
    markedAt: item.markTime || undefined,
    markedBy: item.markBy || undefined,
    name: item.name,
    role: 'EMPLOYEE',
    userId: '',
  }
}

function normalizeBmUserAsset(item: BmUserAssetApiItem): BmUserAssetItem {
  return {
    assetId: item.assetId,
    assetName: item.assetName,
    tasks: item.tasks || [],
  }
}

function normalizeAssetBmMember(item: AssetBmMemberApiItem): AssetBmMemberItem {
  return {
    email: item.email,
    markTypes: (item.markTypes || []).map((markType) => markType.toUpperCase()),
    name: item.name,
    role: item.role,
    userId: item.userId,
  }
}

function normalizeOperationModule(value?: null | string): OperationModule {
  const moduleMap: Record<string, OperationModule> = {
    ASSET: '资产',
    BM_CONFIG: 'BM配置',
    MEMBER: '成员',
    资产: '资产',
    成员: '成员',
    BM配置: 'BM配置',
  }
  return moduleMap[String(value ?? '')] ?? '资产'
}

function normalizeOperationResult(value?: boolean | null | string) {
  return value === true ||
    value === 'ok' ||
    value === 'SUCCESS' ||
    value === 'success' ||
    value === '成功'
    ? 'ok'
    : 'fail'
}

function normalizeOperationDetail(
  detail?: OperationLogItem['detail'],
): OperationLogItem['detail'] {
  if (detail?.kind === 'change') {
    return { fields: detail.fields || [], kind: 'change' }
  }

  return { items: detail?.items || [], kind: 'params' }
}

function normalizeOperationAction(value?: null | string) {
  const actionMap: Record<string, string> = {
    ASSIGN: '分配',
    CANCEL_INTERNAL_MARK: '取消内部标记',
    CREATE_BM: '新增BM',
    DISABLE: '停用',
    EDIT_INTERNAL_MARK: '编辑内部标记',
    ENABLE: '启用',
    MANUAL_SYNC: '手动同步',
    MARK_INTERNAL: '标记内部人员',
    UNASSIGN: '解除',
    UPDATE_BM: '编辑BM',
    UPDATE_TASKS: '更新权限',
  }
  return actionMap[String(value ?? '')] ?? value ?? ''
}

function extractListResponse<T>(
  response: T[] | undefined | { list?: T[]; total?: number },
) {
  const list = Array.isArray(response) ? response : response?.list || []
  const total = Array.isArray(response)
    ? response.length
    : Number(response?.total ?? list.length)

  return { list, total }
}

function extractDataResponse<TData>(
  response: TData | undefined | { data?: TData },
): TData | undefined {
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data
  }

  return response as TData | undefined
}

function compactRecord<T extends Record<string, unknown>>(record: T) {
  return Object.fromEntries(
    Object.entries(record).filter(
      ([, value]) => value !== undefined && value !== '',
    ),
  ) as Partial<T>
}

function normalizeBmSyncBatch(item: BmSyncBatchApiItem): BmSyncBatchItem {
  return {
    addedCount: Number(
      item.addedCount ?? item.lastAddedCount ?? item.added ?? 0,
    ),
    createdAt: item.createdAt ?? item.syncTime ?? item.time ?? '',
    id: item.id,
    removedCount: Number(
      item.removedCount ?? item.lastRemovedCount ?? item.removed ?? 0,
    ),
    triggerType: normalizeBmSyncTriggerType(item.triggerType ?? item.trigger),
  }
}

function normalizeBmSyncTriggerType(value?: null | string): BmSyncTriggerType {
  return value === 'manual' || value === 'MANUAL' || value === '手动'
    ? 'manual'
    : 'scheduled'
}

function normalizeBmSyncChangeType(value?: null | string): BmSyncChangeType {
  return value === 'REMOVED' || value === 'removed' || value === '移除'
    ? 'REMOVED'
    : 'ADDED'
}

function normalizeBmSyncChange(
  item: BmSyncChangeApiItem,
  index: number,
): BmSyncChangeItem {
  return {
    assetType: item.assetType ?? item.type ?? '',
    changeType: normalizeBmSyncChangeType(item.changeType ?? item.change),
    detail: item.detail,
    key: item.id ?? index,
    objectType: item.objectType ?? item.object ?? '',
  }
}

function normalizeMetaAssetUsers(
  users?: MetaAssetUserApiItem[],
): MetaAssetUserItem[] {
  return (users || []).map((user) => ({
    assignmentId: user.assignmentId ?? undefined,
    markTypes: (user.markTypes || []).map((markType) => markType.toUpperCase()),
    tasks: user.tasks || [],
    userEmail: user.userEmail ?? '',
    userId: user.userId ?? '',
    userName: user.userName ?? '',
  }))
}

function mapBmSyncBatchResponse(
  response: QueryBmSyncBatchResponse | undefined,
): PagingResult<BmSyncBatchItem> {
  const { list, total } = extractListResponse(response)
  return {
    list: list.map((item) => normalizeBmSyncBatch(item)),
    total,
  }
}

function mapBmSyncChangeResponse(
  response: QueryBmSyncChangeResponse | undefined,
): PagingResult<BmSyncChangeItem> {
  const { list, total } = extractListResponse(response)
  return {
    list: list.map((item, index) => normalizeBmSyncChange(item, index)),
    total,
  }
}

function normalizeAdAccount(item: MetaAssetApiItem): MetaAssetItem {
  return {
    balance: Number(item.balance ?? 0),
    bmId: item.bmId ?? '',
    bmName: item.bmName ?? '',
    currency: item.currency ?? '',
    customerId: item.customerId ?? '',
    customerName: item.customerName ?? '',
    amOpenId: item.amOpenId ?? '',
    accountId: item.accountId ?? '',
    accountName: item.accountName ?? '',
    lastSyncAt: item.lastSyncAt ?? '',
    bdOpenId: item.bdOpenId ?? '',
    status: item.status,
    timezone: item.timezone ?? '',
    type: 'AD_ACCOUNT',
    users: normalizeMetaAssetUsers(item.users),
  }
}

function mapAdAccountResponse(
  response: QueryAdAccountResponse | undefined,
): PagingResult<MetaAssetItem> {
  const { list, total } = extractListResponse(response)
  return {
    list: list.map((item) => normalizeAdAccount(item)),
    total,
  }
}

function normalizePageAsset(item: QueryPageResponseItem): MetaAssetItem {
  return {
    bmId: item.bmId ?? '',
    bmName: item.bmName ?? '',
    lastSyncAt: item.lastSyncAt ?? '',
    pageId: item.pageId ?? '',
    pageName: item.pageName ?? '',
    type: 'PAGE',
    url: item.url ?? '',
    users: normalizeMetaAssetUsers(item.users),
  }
}

function mapPageResponse(
  response: QueryPageDataResponse | QueryPageResponse | undefined,
): PagingResult<MetaAssetItem> {
  const { list, total } = extractListResponse(
    extractDataResponse<QueryPageDataResponse>(response),
  )
  return {
    list: list.map((item) => normalizePageAsset(item)),
    total,
  }
}

function normalizePixelAsset(item: QueryPixelResponseItem): MetaAssetItem {
  return {
    bmId: item.bmId ?? '',
    bmName: item.bmName ?? '',
    lastSyncAt: item.lastSyncAt ?? '',
    pixelId: item.pixelId ?? '',
    pixelName: item.pixelName ?? '',
    type: 'PIXEL',
    users: normalizeMetaAssetUsers(item.users),
  }
}

function mapPixelResponse(
  response: QueryPixelDataResponse | QueryPixelResponse | undefined,
): PagingResult<MetaAssetItem> {
  const { list, total } = extractListResponse(
    extractDataResponse<QueryPixelDataResponse>(response),
  )
  return {
    list: list.map((item) => normalizePixelAsset(item)),
    total,
  }
}

function normalizeOperationLog(item: OperationLogApiItem): OperationLogItem {
  const detailItems: Array<[string, string]> = [
    ...(item.bmId ? [['BM', item.bmId] as [string, string]] : []),
    ...(item.id === null || item.id === undefined
      ? []
      : [['操作ID', String(item.id)] as [string, string]]),
  ]

  return {
    action: normalizeOperationAction(item.action),
    detail: normalizeOperationDetail({ items: detailItems, kind: 'params' }),
    error: item.errorMsg || undefined,
    id: Number(item.id ?? 0),
    module: normalizeOperationModule(item.module),
    op: item.operator ?? '',
    opId: item.operatorId ?? '',
    result: normalizeOperationResult(item.result),
    summary: item.summary ?? '',
    target: item.target ?? '',
    time: item.createdAt ?? '',
  }
}

function normalizeOperationLogDetail(
  item: OperationLogDetailApiItem,
): OperationLogItem {
  const operationLog = normalizeOperationLog(item)
  const baseDetailItems =
    operationLog.detail.kind === 'params' ? operationLog.detail.items : []
  const params = item.params ?? {}
  const changeList = params.changeList ?? []
  const changeItems = changeList
    .map((change): [string, string, string] => [
      change.label ?? '',
      change.beforeValue ?? '',
      change.afterValue ?? '',
    ])
    .filter((change) => Boolean(change[0]))

  if (params.displayType === 'compare' && changeItems.length > 0) {
    return {
      ...operationLog,
      detail: normalizeOperationDetail({
        fields: changeItems,
        kind: 'change',
      }),
    }
  }

  const valueItems = changeList
    .map((change): [string, string] => [change.label ?? '', change.value ?? ''])
    .filter((detailItem) => Boolean(detailItem[0]) && Boolean(detailItem[1]))

  if (params.displayType === 'value' && valueItems.length > 0) {
    return {
      ...operationLog,
      detail: normalizeOperationDetail({
        items: [...baseDetailItems, ...valueItems],
        kind: 'params',
      }),
    }
  }

  const detailItems: Array<[string, string]> = [
    ['资产', params.asset],
    ['BM', params.bm],
    ['成员', params.member],
    ['task', params.tasks],
    ['失败原因', params.failReason],
    ['重试', params.retry],
    ['Meta 请求', params.metaRequest],
  ].filter((detailItem): detailItem is [string, string] =>
    Boolean(detailItem[1]),
  )

  return {
    ...operationLog,
    detail: normalizeOperationDetail({
      items: [...baseDetailItems, ...detailItems],
      kind: 'params',
    }),
  }
}

function createEmptyOperationLogDetail(): OperationLogItem {
  return {
    action: '无数据',
    detail: { items: [], kind: 'params' },
    id: 0,
    module: '资产',
    op: '',
    opId: '',
    result: 'fail',
    summary: '暂无操作详情数据',
    target: '',
    time: '',
  }
}

function mapOperationLogResponse(
  response: QueryOperationLogResponse | undefined,
): PagingResult<OperationLogItem> {
  const data = Array.isArray(response) ? response : (response?.data ?? response)
  const { list, total } = extractListResponse(data)
  return {
    list: list.map((item) => normalizeOperationLog(item)),
    total,
  }
}

function mapOperationLogDetailResponse(
  response: QueryOperationLogDetailResponse | undefined,
): OperationLogItem {
  const data =
    response && 'data' in response
      ? response.data
      : (response as OperationLogDetailApiItem | undefined)

  if (!data) {
    return createEmptyOperationLogDetail()
  }

  return normalizeOperationLogDetail(data)
}

function mapMemberResponse(
  response: QueryBmUsersResponse | undefined,
): PagingResult<BmMemberItem> {
  const { list, total } = extractListResponse(response)
  return {
    list: list.map((item) => normalizeBmMember(item)),
    total,
  }
}

function mapEmailMarkResponse(
  response: QueryEmailMarksDataResponse | QueryEmailMarksResponse | undefined,
): PagingResult<BmMemberItem> {
  const { list, total } = extractListResponse(
    extractDataResponse<QueryEmailMarksDataResponse>(response),
  )
  return {
    list: list.map((item) => normalizeEmailMark(item)),
    total,
  }
}

function mapAssetBmMembersResponse(
  response:
    | QueryAssetBmMembersDataResponse
    | QueryAssetBmMembersResponse
    | undefined,
): AssetBmMemberItem[] {
  const data = extractDataResponse<QueryAssetBmMembersDataResponse>(response)
  return (data?.list || []).map((item) => normalizeAssetBmMember(item))
}

function mapBmUserAssetsResponse(
  response: QueryBmUserAssetsResponse | undefined,
): BmUserAssetsResult {
  const data =
    extractDataResponse<NonNullable<QueryBmUserAssetsResponse['data']>>(
      response,
    ) ?? {}
  return {
    adAccounts: (data.adAccounts || []).map((item) =>
      normalizeBmUserAsset(item),
    ),
    pages: (data.pages || []).map((item) => normalizeBmUserAsset(item)),
    pixels: (data.pixels || []).map((item) => normalizeBmUserAsset(item)),
  }
}

const bmConfigApi = defineApi('operation', ({ prefix }) => {
  /**
   * BM 配置/查询 BM 列表
   * POST /gobestads/operation/metaAsset/queryBm
   */
  function queryBmList(
    params: QueryBmListParams,
  ): Promise<PagingResult<BmConfigItem>> {
    const condition = params.condition ?? {}
    return requestClient
      .post<QueryBmListResponse>(prefix('metaAsset/queryBm'), {
        condition: {
          bmId: condition.bmId,
          name: condition.name,
          syncEnabled:
            condition.enabled === '' || condition.enabled === undefined
              ? undefined
              : condition.enabled,
          syncState: condition.syncState || undefined,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list: res?.list?.map(normalizeBmConfig) || [],
        total: Number(res?.total ?? 0),
      }))
  }

  /**
   * BM 配置/查询同步批次
   * POST /gobestads/operation/metaAsset/queryBmSyncBatch
   */
  function queryBmSyncBatch(
    params: QueryBmSyncBatchParams,
  ): Promise<PagingResult<BmSyncBatchItem>> {
    return requestClient
      .post<QueryBmSyncBatchResponse>(prefix('metaAsset/queryBmSyncBatch'), {
        condition: {
          bmId: params.bmId,
        },
        pageIndex: params.pageIndex ?? 1,
        pageSize: params.pageSize ?? 10,
      })
      .then(mapBmSyncBatchResponse)
  }

  /**
   * BM 配置/查询同步变更明细
   * POST /gobestads/operation/metaAsset/queryBmSyncChange
   */
  function queryBmSyncChange(
    params: QueryBmSyncChangeParams,
  ): Promise<PagingResult<BmSyncChangeItem>> {
    return requestClient
      .post<QueryBmSyncChangeResponse>(prefix('metaAsset/queryBmSyncChange'), {
        condition: {
          batchId: params.batchId,
          bmId: params.bmId,
        },
        pageIndex: params.pageIndex ?? 1,
        pageSize: params.pageSize ?? 10,
      })
      .then(mapBmSyncChangeResponse)
  }

  /**
   * BM 配置/新增 BM
   * POST /gobestads/operation/metaAsset/createBm
   */
  function createBm(params: CreateBmConfigParams) {
    return requestClient.post<TokenStateResponse>(
      prefix('metaAsset/createBm'),
      params,
    )
  }

  /**
   * BM 配置/编辑 BM
   * POST /gobestads/operation/metaAsset/updateBm
   */
  function updateBm(params: SaveBmConfigParams) {
    return requestClient.post<TokenStateResponse>(
      prefix('metaAsset/updateBm'),
      params,
    )
  }

  /**
   * BM 配置/重新校验 Token
   * POST /gobestads/operation/metaAsset/validateBmToken
   */
  function validateBmToken(params: ValidateBmTokenParams) {
    return requestClient.post<TokenStateResponse>(
      prefix('metaAsset/validateBmToken'),
      params,
    )
  }

  /**
   * BM 配置/启停 BM 同步
   * POST /gobestads/operation/metaAsset/toggleBmSync
   */
  function toggleBmSync(params: ToggleBmSyncParams) {
    return requestClient.post(prefix('metaAsset/toggleBmSync'), params)
  }

  /**
   * BM 配置/手动同步
   * POST /gobestads/operation/metaAsset/triggerBmSync
   */
  function triggerBmSync(params: TriggerBmSyncParams) {
    return requestClient.post<TriggerBmSyncResponse>(
      prefix('metaAsset/triggerBmSync'),
      params,
    )
  }

  return {
    createBm,
    queryBmList,
    queryBmSyncBatch,
    queryBmSyncChange,
    toggleBmSync,
    triggerBmSync,
    updateBm,
    validateBmToken,
  }
})

const assetApi = defineApi('operation', ({ prefix }) => {
  /**
   * 资产/Ad Account 列表查询
   * POST /gobestads/operation/metaAsset/queryAdAccount
   */
  function queryAdAccount(
    params: QueryAssetsParams,
  ): Promise<PagingResult<MetaAssetItem>> {
    return requestClient
      .post<QueryAdAccountResponse>(
        prefix('metaAsset/queryAdAccount'),
        buildQueryAssetsRequestParams(params),
      )
      .then(mapAdAccountResponse)
  }

  /**
   * 资产/Page 列表查询
   * POST /gobestads/operation/metaAsset/queryPage
   */
  function queryPage(
    params: QueryAssetsParams,
  ): Promise<PagingResult<MetaAssetItem>> {
    return requestClient
      .post<QueryPageResponse>(
        prefix('metaAsset/queryPage'),
        buildQueryAssetsRequestParams(params),
      )
      .then(mapPageResponse)
  }

  /**
   * 资产/Pixel 列表查询
   * POST /gobestads/operation/metaAsset/queryPixel
   */
  function queryPixel(
    params: QueryAssetsParams,
  ): Promise<PagingResult<MetaAssetItem>> {
    return requestClient
      .post<QueryPixelResponse>(
        prefix('metaAsset/queryPixel'),
        buildQueryAssetsRequestParams(params),
      )
      .then(mapPixelResponse)
  }

  return { queryAdAccount, queryPage, queryPixel }
})

export function buildQueryAssetsRequestParams(params: QueryAssetsParams) {
  const condition = params.condition ?? { type: 'AD_ACCOUNT' }
  const paging = {
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
  }

  if (condition.type === 'PAGE') {
    return {
      condition: compactRecord({
        assigned: condition.assigned,
        bmId: condition.bmId,
        bmName: condition.bmName,
        member: condition.member,
        pageId: condition.assetId,
        pageName: condition.assetName,
        url: condition.url,
      }),
      ...paging,
    }
  }

  if (condition.type === 'PIXEL') {
    return {
      condition: compactRecord({
        assigned: condition.assigned,
        bmId: condition.bmId,
        bmName: condition.bmName,
        member: condition.member,
        pixelId: condition.assetId,
        pixelName: condition.assetName,
      }),
      ...paging,
    }
  }

  return {
    condition: compactRecord({
      accountId: condition.assetId,
      accountName: condition.assetName,
      assigned: condition.assigned,
      amOpenIds: condition.amOpenIds?.length ? condition.amOpenIds : undefined,
      bmId: condition.bmId,
      bmName: condition.bmName,
      customerId: condition.customerId,
      customerName: condition.customerName,
      member: condition.member,
      bdOpenIds: condition.bdOpenIds?.length ? condition.bdOpenIds : undefined,
      status: condition.status || undefined,
    }),
    ...paging,
  }
}

function queryAssets(
  params: QueryAssetsParams,
): Promise<PagingResult<MetaAssetItem>> {
  const condition = params.condition ?? { type: 'AD_ACCOUNT' }

  if (condition.type === 'AD_ACCOUNT') {
    return assetApi.queryAdAccount(params)
  }

  if (condition.type === 'PAGE') {
    return assetApi.queryPage(params)
  }

  if (condition.type === 'PIXEL') {
    return assetApi.queryPixel(params)
  }

  return Promise.resolve({ list: [], total: 0 })
}

const metaAssetOperationApi = defineApi('operation', ({ prefix }) => {
  function queryAssetBmMembers(
    params: QueryAssetBmMembersParams,
  ): Promise<AssetBmMemberItem[]> {
    return requestClient
      .post<QueryAssetBmMembersResponse>(
        prefix('metaAsset/queryAssetBmMembers'),
        params,
      )
      .then(mapAssetBmMembersResponse)
  }

  function queryBmUserAssets(
    params: QueryBmUserAssetsParams,
  ): Promise<BmUserAssetsResult> {
    return requestClient
      .post<QueryBmUserAssetsResponse>(
        prefix('metaAsset/queryBmUserAssets'),
        params,
      )
      .then(mapBmUserAssetsResponse)
  }

  function queryBmUsers(
    params: QueryMembersParams,
  ): Promise<PagingResult<BmMemberItem>> {
    const condition = params.condition ?? {}
    const userEmail = condition.userEmail ?? condition.email
    const isInternal = condition.isInternal ?? condition.internal
    return requestClient
      .post<QueryBmUsersResponse>(prefix('metaAsset/queryBmUser'), {
        condition: {
          bmId: condition.bmId,
          bmName: condition.bmName,
          isInternal:
            isInternal === '' || isInternal === undefined
              ? undefined
              : isInternal,
          role: condition.role || undefined,
          userEmail,
          userName: condition.name,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then(mapMemberResponse)
  }

  function queryEmailMarks(
    params: QueryMembersParams,
  ): Promise<PagingResult<BmMemberItem>> {
    const condition = params.condition ?? {}
    const userEmail = condition.userEmail ?? condition.email
    const isInternal = condition.isInternal ?? condition.internal
    return requestClient
      .post<QueryEmailMarksResponse>(prefix('metaAsset/queryEmailMark'), {
        condition: {
          isInternal:
            isInternal === '' || isInternal === undefined
              ? undefined
              : isInternal,
          userName: condition.name,
          userEmail,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then(mapEmailMarkResponse)
  }

  function queryOperationLogs(
    params: QueryOperationLogsParams,
  ): Promise<PagingResult<OperationLogItem>> {
    const condition = params.condition ?? {}

    return requestClient
      .post<QueryOperationLogResponse>(prefix('metaAsset/queryOperationLog'), {
        condition: compactRecord({
          action: toOperationActionCode(condition.action),
          dateRange: condition.dateRange,
          module: toOperationModuleCode(condition.module),
          operator: condition.op,
          result: toOperationResultValue(condition.result),
          target: condition.target,
        }),
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then(mapOperationLogResponse)
  }

  function queryOperationLogDetail(
    params: QueryOperationLogDetailParams,
  ): Promise<OperationLogItem> {
    return requestClient
      .post<QueryOperationLogDetailResponse>(
        prefix('metaAsset/queryOperationLogDetail'),
        { id: params.id },
      )
      .then(mapOperationLogDetailResponse)
  }

  function unassignAsset(params: UnassignAssetParams) {
    return requestClient.post<MetaAssetWriteResponse>(
      prefix('metaAsset/unassignAsset'),
      params,
    )
  }

  function updateAssetTasks(params: UpdateAssetTasksParams) {
    return requestClient.post<MetaAssetWriteResponse>(
      prefix('metaAsset/updateAssetTasks'),
      params,
    )
  }

  function assignAsset(params: AssignAssetParams) {
    return requestClient.post<MetaAssetWriteResponse>(
      prefix('metaAsset/assignAsset'),
      params,
    )
  }

  function saveEmailMark(params: SaveEmailMarkParams) {
    return requestClient.post<MetaAssetWriteResponse>(
      prefix('metaAsset/saveEmailMark'),
      params,
    )
  }

  return {
    assignAsset,
    queryAssetBmMembers,
    queryBmUserAssets,
    queryBmUsers,
    queryEmailMarks,
    queryOperationLogDetail,
    queryOperationLogs,
    saveEmailMark,
    unassignAsset,
    updateAssetTasks,
  }
})

export const metaAssetApi = {
  ...bmConfigApi,
  ...metaAssetOperationApi,
  queryAssets,
}
